import * as Dialog from '@wandb/weave/components/Dialog';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {IconName} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useMemo} from 'react';

import {useSemanticLegendSettingsContext} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {nwToast} from '../../../state/workspaces/namedWorkspaceToast';
import {useDetectPanelVariant, useGoToBaseUrl} from '../../Panel/hooks/hooks';
import {useIsWorkspaceTemplateActionDisplayed} from '../../WorkspaceTemplate/useIsWorkspaceTemplateEnabled';
import {WorkspaceTemplateDrawer} from '../../WorkspaceTemplate/WorkspaceTemplateDrawer';
import {
  useWorkspaceTemplateDrawerContext,
  WorkspaceTemplateDrawerContextProvider,
} from '../../WorkspaceTemplate/WorkspaceTemplateDrawerContextProvider';
import {
  useViewBarActionContext,
  useViewBarContext,
} from '../NamedWorkspaceViewBarContext';
import {
  getDisabledTooltipContent,
  getResetTooltipContent,
  parseAndTrackNamedWorkspaces,
} from '../utils';
import {makeOverflowMenuItem, OverflowMenu} from './OverflowMenu';
import {useSaveAndCopyActions} from './ViewBarButton';
import {WorkspaceTemplateOverflowMenuItem} from './WorkspaceTemplateOverflowMenuItem';

const ANONYMOUS_PROJECT_TOOLTIP_CONTENT =
  'Log in or create an account to perform workspace actions.';
const COPY_WORKSPACE_URL_TOOLTIP_CONTENT =
  'You can share personal workspaces and saved views with colleagues by copying their URL in the address bar.';

type OverflowMenuItemProps = {
  displayName: string;
  icon: IconName;
  onSelect: (e: Event) => void;
  disabled?: boolean;
  tooltipContent?: string;
  className?: string;
  children?: React.ReactNode;
} & Record<string, any>;

export const NamedWorkspaceOverflowMenu = () => {
  const {viewer, currentWorkspaceType} = useViewBarContext();
  const isAnonymousProject = viewer == null;

  const {isWorkspaceTemplateActionDisplayed, isLoadingEntityType} =
    useIsWorkspaceTemplateActionDisplayed(currentWorkspaceType);

  return (
    <WorkspaceTemplateDrawerContextProvider
      isWorkspaceTemplateActionDisplayed={isWorkspaceTemplateActionDisplayed}>
      <OverflowMenu
        aria-label="Workspace actions"
        data-test="nw-overflow-menu-trigger"
        isDisabled={isAnonymousProject || isLoadingEntityType}
        variant="secondary"
        tooltip={
          isAnonymousProject ? ANONYMOUS_PROJECT_TOOLTIP_CONTENT : undefined
        }>
        <WorkspaceOverflowActions />
      </OverflowMenu>
      <WorkspaceTemplateDrawer />
    </WorkspaceTemplateDrawerContextProvider>
  );
};

const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  const {isWorkspaceTemplateActionDisplayed} =
    useWorkspaceTemplateDrawerContext();
  const MenuItem = useMemo(
    () =>
      makeOverflowMenuItem({
        tooltipProps: {
          side: 'left',
          sideOffset: isWorkspaceTemplateActionDisplayed ? 280 : 205,
        },
      }),
    [isWorkspaceTemplateActionDisplayed]
  );
  return <MenuItem {...props} />;
};

const ForwardedOverflowMenuItem = React.forwardRef<
  typeof DropdownMenu.Item,
  {
    displayName: string;
    icon: IconName;
    onSelect: (e: Event) => void;
    [x: string]: any;
  }
>((props, ref) => {
  return <OverflowMenuItem {...props} />;
});

const WorkspaceOverflowActions = () => {
  const {
    saveAsNewWorkspace,
    currentView,
    viewer,
    resetToDefaultSpec,
    isLoadingWorkspaceTemplates,
  } = useViewBarContext();
  const {semanticLegendSettings, semanticLegendRunColorOptions} =
    useSemanticLegendSettingsContext();
  const {setModalDetails} = useViewBarActionContext();
  const {copy, save} = useSaveAndCopyActions();
  const {saveAsNewWorkspaceInfo, resetToDefaultSpecInfo, deleteViewInfo} =
    useOverflowMenuActions();

  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const goToWorkspaceBaseUrl = useGoToBaseUrl();

  const shouldShowSeparator =
    resetToDefaultSpecInfo.isDisplayed || deleteViewInfo.isDisplayed;

  return (
    <>
      {save.isDisplayed && (
        <Dialog.Trigger asChild className="flex xl:hidden">
          <ForwardedOverflowMenuItem
            displayName="Save view"
            icon="save"
            disabled={save.isUiDisabled}
            onSelect={save.fn}
          />
        </Dialog.Trigger>
      )}
      <OverflowMenuItem
        displayName="Copy workspace URL"
        icon="copy"
        onSelect={() => {
          parseAndTrackNamedWorkspaces({
            action: 'Copy workspace url',
            location: 'menu',
            currentView,
            viewer,
          });
          copyToClipboard(window.location.href);
          nwToast('Copied to clipboard');
        }}
        tooltipContent={COPY_WORKSPACE_URL_TOOLTIP_CONTENT}
      />
      {saveAsNewWorkspaceInfo.isDisplayed && (
        <OverflowMenuItem
          displayName="Save as new view"
          icon="add-new"
          disabled={saveAsNewWorkspaceInfo.isDisabled}
          onSelect={() => {
            parseAndTrackNamedWorkspaces({
              action: 'Save as new view',
              location: 'menu',
              currentView,
              viewer,
            });
            saveAsNewWorkspace?.({
              ...semanticLegendSettings,
              runColorOptions: semanticLegendRunColorOptions,
            });
          }}
          tooltipContent={
            saveAsNewWorkspaceInfo.isDisabled
              ? getDisabledTooltipContent(viewer)
              : ''
          }
        />
      )}
      <WorkspaceTemplateOverflowMenuItem />
      {copy.isDisplayed && (
        <OverflowMenuItem
          className="flex xl:hidden"
          displayName="Copy to my workspace"
          icon="forward-next"
          onSelect={copy.fn}
        />
      )}
      {shouldShowSeparator && <DropdownMenu.Separator />}
      {resetToDefaultSpecInfo.isDisplayed && (
        <OverflowMenuItem
          disabled={resetToDefaultSpecInfo.isDisabled}
          displayName="Reset workspace"
          icon="randomize-reset-reload"
          onSelect={() => {
            parseAndTrackNamedWorkspaces({
              action: 'Reset workspace',
              location: 'menu',
              currentView,
              viewer,
            });
            resetToDefaultSpec?.();
            if (isFullScreenPanelPage) {
              goToWorkspaceBaseUrl();
            }
          }}
          tooltipContent={getResetTooltipContent({
            isLoadingWorkspaceTemplates,
            resetToDefaultSpecInfoIsDisabled: resetToDefaultSpecInfo.isDisabled,
            viewer,
          })}
        />
      )}
      {deleteViewInfo.isDisplayed && (
        <Dialog.Trigger asChild>
          <ForwardedOverflowMenuItem
            displayName="Delete view"
            icon="delete"
            disabled={deleteViewInfo.isDisabled}
            onSelect={() => {
              setModalDetails({
                type: 'delete view',
              });
            }}
            tooltipContent={
              deleteViewInfo.isDisabled && getDisabledTooltipContent(viewer)
            }
          />
        </Dialog.Trigger>
      )}
    </>
  );
};

export const useOverflowMenuActions = () => {
  const {
    currentView,
    workspaceType,
    shouldRevokeWritePerms,
    resetToDefaultSpec,
    saveAsNewWorkspace,
    deleteViewAndLoad,
  } = useViewBarContext();

  // if wb admin has powers on, saveAsNewWorkspace and deleteView should be displayed
  // but disabled to indicate they need to exit admin mode to make write changes.
  return useMemo(
    () => ({
      saveAsNewWorkspaceInfo: {
        isDisplayed: saveAsNewWorkspace != null || shouldRevokeWritePerms,
        isDisabled: shouldRevokeWritePerms || currentView?.saving,
      },
      resetToDefaultSpecInfo: {
        isDisplayed: resetToDefaultSpec != null,
        isDisabled: resetToDefaultSpec == null,
      },
      deleteViewInfo: {
        isDisplayed:
          workspaceType !== 'personal' &&
          (deleteViewAndLoad != null || shouldRevokeWritePerms),
        isDisabled: shouldRevokeWritePerms,
      },
    }),
    [
      currentView?.saving,
      deleteViewAndLoad,
      resetToDefaultSpec,
      saveAsNewWorkspace,
      shouldRevokeWritePerms,
      workspaceType,
    ]
  );
};
